import React, { useState } from 'react';

import { Box, HStack } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { useEtaCounter } from 'hooks/use-eta-counter';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import usePolling from 'hooks/use-polling';
import fetchDeliveryStatus from 'remote/api/delivery-status';
import { useDeliveryConfirmationContext } from 'state/delivery-confirmation';
import { DeliveryStatus } from 'state/order/types';

interface IDeliveryDetailsProps {
  serverOrder: IServerOrder;
}

const DeliveryDetails: React.FC<React.PropsWithChildren<IDeliveryDetailsProps>> = ({
  serverOrder,
}) => {
  const { delivery, rbiOrderId } = serverOrder;
  const isMounted = useIsComponentMounted();
  const [deliveryStatus, setDeliveryStatus] = useState(delivery!.status);
  const [dropoff, setDropoff] = useState(delivery?.dropoff);
  const { formatMessage } = useIntl();
  const eta = useEtaCounter(deliveryStatus, dropoff);
  const { setBringgUuids } = useDeliveryConfirmationContext();

  const { stopPolling } = usePolling({
    queryOptions: fetchDeliveryStatus(rbiOrderId),
    interval: 5000,
    retry: 3,
    onResponse: data => {
      if (isMounted) {
        setDropoff(data?.delivery?.dropoff);
        setDeliveryStatus(data?.delivery?.status);
        // Needed to track bringg delivery driver coords
        const share_uuid = data?.delivery?.shareUuid;
        const order_uuid = data?.delivery?.task?.uuid;
        setBringgUuids({ share_uuid, order_uuid });
      }
    },
    onError: () => {
      stopPolling();
    },
  });

  const getDeliveryEtaText = (status: DeliveryStatus): string => {
    switch (status) {
      case DeliveryStatus.DRIVER_AT_CUSTOMER:
        return formatMessage({ id: 'deliveryDriverArrived' });
      case DeliveryStatus.ORDER_DROPPED_OFF:
        return formatMessage({ id: 'deliveryDelivered' });
      default:
        return eta || '...';
    }
  };

  return (
    <HStack h={'$8'}>
      <Box>{`${formatMessage({ id: 'eta' })}: `}</Box>
      <Box flexWrap={'wrap'}>{getDeliveryEtaText(deliveryStatus)}</Box>
    </HStack>
  );
};

export default DeliveryDetails;
